import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { useAsyncFn } from 'react-use';
import { CreateTaskFromIssueRequest } from '../types';
import { SEV2_BACKSTAGE_ERROR_TRACKING_PROJECT_ID } from '../constants';

export const useCreateTask = () => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [createTaskState, createTask] = useAsyncFn(async (params: CreateTaskFromIssueRequest) => {
    console.log('Creating task with params:', params);
    
    try {      
      // Step 1: Get project phid
      console.log('Getting project PHID for project ID:', params.project);
      const projectSearchBody = {
        'constraints[ids][0]': params.project,
        'constraints[ids][1]': SEV2_BACKSTAGE_ERROR_TRACKING_PROJECT_ID,
      };

      console.log('Calling sev2-backend project.search');
      const projectResponse = await fetchApi.fetch(`${backendUrl}/api/sev2/proxy/project.search`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectSearchBody)
      });

      console.log('Project search response status:', projectResponse.status);

      if (!projectResponse.ok) {
        const errorText = await projectResponse.text();
        console.error('Failed to get project PHID:', projectResponse.status, errorText);
        throw new Error(`Failed to get project PHID: ${projectResponse.status}`);
      }

      const projectData = await projectResponse.json();
      console.log('Project search result:', projectData);

      if (projectData.error_code) {
        throw new Error(`Phabricator project search error: ${projectData.error_code} - ${projectData.error_info}`);
      }

      if (!projectData.result?.data?.[0]?.phid) {
        console.error('No project found with ID:', params.project);
        throw new Error(`Project with ID ${params.project} not found`);
      }

      const projectPHID = projectData.result.data[0].phid;
      console.log('Found project PHID:', projectPHID);

      const backstageErrorTrackingPHID = projectData.result.data[1].phid;
      
      // Step 2: Create the task
      const taskRequestBody = {
        'transactions[0][type]': 'title',
        'transactions[0][value]': params.title,
        'transactions[1][type]': 'description',
        'transactions[1][value]': params.description,
        'transactions[2][type]': 'priority',
        'transactions[2][value]': params.priority,
        'transactions[3][type]': 'projects.set',
        'transactions[3][value][0]': projectPHID,
        'transactions[3][value][1]': backstageErrorTrackingPHID,
        // 'transactions[4][type]': 'subtype',
        // 'transactions[4][value]': params.subtype,
      };

      console.log('Calling sev2-backend maniphest.edit');
      const taskResponse = await fetchApi.fetch(`${backendUrl}/api/sev2/proxy/maniphest.edit`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskRequestBody)
      });

      console.log('Task creation response status:', taskResponse.status);

      if (!taskResponse.ok) {
        const errorText = await taskResponse.text();
        console.error('Failed to create task:', taskResponse.status, errorText);
        throw new Error(`Failed to create task: ${taskResponse.status}`);
      }

      const result = await taskResponse.json();
      console.log('Task created successfully:', result);
      
      if (result.error_code) {
        throw new Error(`Phabricator task creation error: ${result.error_code} - ${result.error_info}`);
      }

      return result;
    } catch (err) {
      console.error('Error creating task:', err);
      throw err;
    }
  }, [fetchApi]);

  return {
    createTask,
    isCreating: createTaskState.loading,
    error: createTaskState.error,
    result: createTaskState.value
  };
};
